
import { 
  Card, 
  CardContent,
  CardFooter
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { DollarSign, AlertTriangle, Truck, TrendingUp, TrendingDown } from "lucide-react";

interface SupplierScoreCardProps {
  supplier: {
    id: number;
    name: string;
    category: string;
    priceScore: number;
    reliabilityScore: number;
    deliveryScore: number;
    overallScore: number;
    priceChange: number;
    lastOrderDate: string;
    aboveMarketRate: boolean;
    competitorsHaveBetterRates: boolean;
    volumeDiscountEligible: boolean;
  };
}

export const SupplierScoreCard = ({ supplier }: SupplierScoreCardProps) => {
  const getScoreColor = (score: number) => {
    if (score >= 90) return "bg-green-600";
    if (score >= 80) return "bg-green-500";
    if (score >= 70) return "bg-yellow-500";
    if (score >= 60) return "bg-orange-500";
    return "bg-red-500";
  };

  const getBadgeVariant = (score: number) => {
    if (score >= 80) return "default";
    if (score >= 70) return "secondary";
    return "destructive";
  };

  return (
    <Card className="overflow-hidden border">
      <div className={`h-2 ${getScoreColor(supplier.overallScore)}`} />
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-lg">{supplier.name}</h3>
              <p className="text-muted-foreground text-sm">{supplier.category}</p>
            </div>
            <Badge variant={getBadgeVariant(supplier.overallScore)} className="text-lg px-2 py-1">
              {supplier.overallScore}
            </Badge>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 mr-1 text-muted-foreground" />
                <span className="text-sm">Price</span>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={supplier.priceScore} className="w-20 h-2" />
                <span className="text-sm font-medium">{supplier.priceScore}</span>
                {supplier.priceChange !== 0 && (
                  supplier.priceChange > 0 ? (
                    <Badge variant="destructive" className="flex items-center h-5 text-xs">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      {supplier.priceChange}%
                    </Badge>
                  ) : (
                    <Badge variant="default" className="flex items-center h-5 text-xs bg-green-600">
                      <TrendingDown className="h-3 w-3 mr-1" />
                      {Math.abs(supplier.priceChange)}%
                    </Badge>
                  )
                )}
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1 text-muted-foreground" />
                <span className="text-sm">Reliability</span>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={supplier.reliabilityScore} className="w-20 h-2" />
                <span className="text-sm font-medium">{supplier.reliabilityScore}</span>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <Truck className="h-4 w-4 mr-1 text-muted-foreground" />
                <span className="text-sm">Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <Progress value={supplier.deliveryScore} className="w-20 h-2" />
                <span className="text-sm font-medium">{supplier.deliveryScore}</span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className="bg-muted/30 p-2 flex justify-between text-xs">
        <span>Last order: {supplier.lastOrderDate}</span>
        {supplier.aboveMarketRate && (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
            Above market rate
          </Badge>
        )}
      </CardFooter>
    </Card>
  );
};
